import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Pencil, ShieldCheck, ShieldOff } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { useSetApplePayIntegratorStatus } from '@/lib/api/apple-pay-platform';

import { IntegratorForm } from './IntegratorForm';
import { AssociationFileDialog } from './AssociationFileDialog';

type Environment = 'production' | 'sandbox';

interface Integrator {
  id: string;
  ppiid: string;
  label: string;
  associationFileContent: string | null;
  btMerchantRegistrationId: string | null;
  status: 'pending_certs' | 'active' | 'disabled';
}

/** One labelled value in the read-only summary. */
function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="space-y-1">
      <dt className="text-muted-foreground text-xs font-medium uppercase tracking-wide">{label}</dt>
      <dd className={mono ? 'break-all font-mono text-sm' : 'text-sm'}>{value}</dd>
    </div>
  );
}

/**
 * The platform integrator, in whichever of its two states applies.
 *
 * Unconfigured, this is the page's only job, so the form is the card. Once
 * configured it collapses to a read-only summary — the values are set once and
 * changing them mid-flight (a different PPIID, a different association file)
 * would deverify every registered domain, so editing is deliberately behind a
 * dialog rather than sitting on screen inviting a stray keystroke.
 */
export function IntegratorPanel({
  environment,
  integrator,
}: {
  environment: Environment;
  integrator: Integrator | null;
}) {
  const { t } = useTranslation('manageApplePay');
  const setStatus = useSetApplePayIntegratorStatus();
  const { isSuperAdmin } = useSessionClaims();
  const [isEditing, setIsEditing] = useState(false);

  if (!integrator) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('integrator.setupTitle')}</CardTitle>
          <CardDescription>{t('integrator.setupDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {isSuperAdmin ? (
            <IntegratorForm environment={environment} submitLabel={t('integrator.setupSubmit')} />
          ) : (
            <p className="text-muted-foreground text-sm">{t('integrator.superadminOnly')}</p>
          )}
        </CardContent>
      </Card>
    );
  }

  const isDisabled = integrator.status === 'disabled';
  const isReady = integrator.status === 'active' && !!integrator.associationFileContent;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              {integrator.label}
              <Badge variant={isReady ? 'default' : isDisabled ? 'destructive' : 'secondary'}>
                {t(`integrator.statuses.${integrator.status}`)}
              </Badge>
            </CardTitle>
            <CardDescription>{t('integrator.summaryDescription')}</CardDescription>
          </div>

          <div className="flex items-center gap-2">
            {integrator.associationFileContent && (
              <AssociationFileDialog content={integrator.associationFileContent} />
            )}

            {/* Editing changes the PPIID or the verification file, which
                invalidates every domain already registered with Apple — the same
                platform-wide blast radius as the switch below, so the same gate.
                Mirrors `superadminProcedure` on upsertIntegrator. */}
            {isSuperAdmin && (
            <Dialog open={isEditing} onOpenChange={setIsEditing}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  {t('integrator.edit')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{t('integrator.editTitle')}</DialogTitle>
                  <DialogDescription>{t('integrator.editDescription')}</DialogDescription>
                </DialogHeader>
                <IntegratorForm
                  environment={environment}
                  initial={integrator}
                  submitLabel={t('integrator.save')}
                  onSaved={() => setIsEditing(false)}
                />
              </DialogContent>
            </Dialog>
            )}

            {/* Superadmin only, mirroring `superadminProcedure` on
                setIntegratorStatus. Disabling drops every PPIID domain out of
                the payment setup at once, so Apple Pay goes dark platform-wide
                rather than for one merchant. The server is the real gate; this
                keeps the control out of reach of anyone who cannot use it. */}
            {isSuperAdmin && (
              <Button
                variant={isDisabled ? 'default' : 'ghost'}
                size="sm"
                disabled={setStatus.isPending}
                onClick={() => setStatus.mutate({ environment, status: isDisabled ? 'active' : 'disabled' })}
              >
                {isDisabled ? (
                  <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />
                ) : (
                  <ShieldOff className="mr-1.5 h-3.5 w-3.5" />
                )}
                {isDisabled ? t('integrator.enable') : t('integrator.disable')}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {isDisabled && (
          <p className="text-destructive flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {t('integrator.disabled')}
          </p>
        )}

        <dl className="grid gap-4 sm:grid-cols-2">
          <Field label={t('integrator.ppiid')} value={integrator.ppiid} mono />
          <Field
            label={t('integrator.btRegistration')}
            value={
              integrator.btMerchantRegistrationId ?? (
                <span className="text-muted-foreground">{t('integrator.btRegistrationMissing')}</span>
              )
            }
            mono
          />
          <Field
            label={t('integrator.associationFile')}
            value={
              integrator.associationFileContent ? (
                <span className="flex items-center gap-1.5 text-emerald-600">
                  <Check className="h-3.5 w-3.5" />
                  {t('integrator.associationFileSet', {
                    length: integrator.associationFileContent.length,
                  })}
                </span>
              ) : (
                <span className="text-destructive">{t('integrator.associationFileMissing')}</span>
              )
            }
          />
        </dl>
      </CardContent>
    </Card>
  );
}
